import { FileText, FileSpreadsheet, ListChecks, ClipboardCheck, Code2, Scale } from "lucide-react";
import { useFlowReveal } from "@/hooks/useFlowReveal";

// Each item is a document the platform actually produces (report types and
// exports in the audit tool), so the page shows the work, not just the claim.
const deliverables = [
  {
    icon: Scale,
    title: "Scope and fee, before we start",
    body: "We crawl your site and count pages and PDF pages first, so the engagement letter is priced on what you actually have.",
  },
  {
    icon: FileText,
    title: "Executive report",
    body: "A score, severity breakdown and plain-language summary that leadership and compliance teams can read in minutes.",
  },
  {
    icon: Code2,
    title: "Developer report",
    body: "Every issue with its WCAG criterion, the affected element, a screenshot and the recommended fix.",
  },
  {
    icon: ClipboardCheck,
    title: "Manual verification",
    body: "Certified auditors confirm or dismiss automated findings and add what tools cannot detect, such as keyboard and screen reader flows.",
  },
  {
    icon: ListChecks,
    title: "Action plan",
    body: "A prioritised remediation roadmap, as a summary or in detail, so fixes are done in the order that reduces risk fastest.",
  },
  {
    icon: FileSpreadsheet,
    title: "Excel export",
    body: "The full issue list in a spreadsheet your team can assign, filter and track through remediation.",
  },
];

const DeliverablesSection = () => {
  const flowRef = useFlowReveal<HTMLUListElement>();

  return (
  <section id="deliverables" className="section-padding section-alt" aria-labelledby="deliverables-heading">
    <div className="max-w-7xl mx-auto">
      <p className="eyebrow mb-4">What you receive</p>
      <h2 id="deliverables-heading" className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 max-w-3xl">
        Documents you can act on, not just a score
      </h2>
      <p className="mb-10 max-w-2xl text-lg text-muted-foreground leading-relaxed">
        Every audit ends with reports written for the people who need them: leadership, developers and procurement.
      </p>
      <ul ref={flowRef} className="grid list-none gap-x-12 gap-y-8 md:grid-cols-2 lg:grid-cols-3">
        {deliverables.map((d, i) => (
          <li key={d.title} className="flow-step flex gap-4 border-t pt-5" style={{ "--i": Math.min(i, 5) } as React.CSSProperties}>
            <d.icon size={22} className="mt-1 shrink-0 text-primary" aria-hidden="true" />
            <div>
              <h3 className="font-display text-lg font-bold text-foreground">{d.title}</h3>
              <p className="mt-1 text-base leading-relaxed text-muted-foreground">{d.body}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  </section>
  );
};

export default DeliverablesSection;
